import { useMemo, useState } from "react";
import { Link } from "react-router";
import { EmptyState, ErrorPanel, Loading, PageHeader, formatDate } from "../components/ui";
import { useAuth } from "../lib/auth";
import { useProfile } from "../lib/queries";

const KIND_LABEL = { reading: "Reading", vocabulary: "Vocabulary", writing: "Writing" } as const;

/** Points totals, recent awards, and My Words (CONCEPT.md §16.5). */
export function ProfilePage() {
  const { user } = useAuth();
  const q = useProfile();
  const [search, setSearch] = useState("");

  const words = useMemo(() => {
    const list = q.data?.myWords ?? [];
    const s = search.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (w) => w.word.toLowerCase().includes(s) || (w.meaning ?? "").toLowerCase().includes(s) || w.storyTitle.toLowerCase().includes(s),
    );
  }, [q.data, search]);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  const { totals, recent, myWords } = q.data;

  return (
    <div className="page">
      <PageHeader title={user?.displayName ?? "Profile"} subtitle="Your reading, writing, and vocabulary progress." />

      <div className="profile-tiles">
        <div className="profile-tile all">
          <div className="n">{totals.all}</div>
          <div className="k">All points</div>
        </div>
        {(["reading", "vocabulary", "writing"] as const).map((k) => (
          <div key={k} className="profile-tile">
            <div className="n">{totals[k]}</div>
            <div className="k">{KIND_LABEL[k]}</div>
          </div>
        ))}
      </div>

      <section className="section">
        <h2>My Words</h2>
        {myWords.length === 0 ? (
          <EmptyState title="No words yet.">
            Play a story with learning mode on. Every word you match or use correctly lands here.{" "}
            <Link to="/stories">Find a story</Link>
          </EmptyState>
        ) : (
          <>
            <input
              type="search"
              className="mywords-search"
              placeholder="Search words, meanings, stories"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search my words"
            />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Word</th>
                    <th>Meaning</th>
                    <th>Story</th>
                    <th>How</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {words.map((w, i) => (
                    <tr key={`${w.word}-${w.kind}-${i}`}>
                      <td>
                        <strong>{w.word}</strong>
                      </td>
                      <td>{w.meaning ?? <span className="muted">—</span>}</td>
                      <td>
                        <Link to={`/stories/${w.storyId}`}>{w.storyTitle}</Link>
                      </td>
                      <td>
                        <span className="how-pill">{w.kind === "vocab_match" ? "matched" : "used in writing"}</span>
                      </td>
                      <td className="muted small">{formatDate(w.earnedAt)}</td>
                    </tr>
                  ))}
                  {words.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted small">
                        No words match “{search}”.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="section">
        <h2>Recent</h2>
        {recent.length === 0 ? (
          <p className="muted small">Nothing yet.</p>
        ) : (
          <ul className="plain-list">
            {recent.map((r, i) => (
              <li key={i}>
                <span className="vocab-points">+{r.points}</span> {KIND_LABEL[r.kind]}
                {r.word ? (
                  <>
                    {" "}
                    for <strong>{r.word}</strong>
                  </>
                ) : null}{" "}
                in <Link to={`/stories/${r.storyId}`}>{r.storyTitle}</Link>{" "}
                <span className="muted small">{formatDate(r.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
